// lib/workspace-card-selectors.ts
import { getLayerWorkspaceId } from '#types/types';
import type { KernelState } from '#types/types';
import { sortLayersByRenderOrder } from '#lib/layer-render-order';

export const selectOrderedWorkspaceLayerIdsSignature = (
  state: KernelState,
  workspaceId: string
): string => {
  const workspace = state.inventory.StudioWorkspace.registry[workspaceId];
  const layerIds = Array.isArray(workspace?.layerIds) ? workspace.layerIds : [];
  const projectId = typeof workspace?.projectId === 'string' ? workspace.projectId.trim() : '';
  if (layerIds.length === 0 || !projectId) return '';

  const layers = layerIds
    .map((layerId) => {
      const layer = state.inventory.StudioLayer.registry[layerId];
      if (
        !layer ||
        layer.projectId.trim() !== projectId ||
        getLayerWorkspaceId(layer) !== workspaceId
      ) return null;
      const legacyTopLayer = Reflect.get(layer, 'top-layer') === true;
      return {
        id: layer.id,
        renderOrder: typeof layer.renderOrder === 'number' && Number.isFinite(layer.renderOrder)
          ? layer.renderOrder
          : undefined,
        'top-layer': legacyTopLayer || layer.topLayer === true,
      };
    })
    .filter((layer): layer is NonNullable<typeof layer> => Boolean(layer));

  return sortLayersByRenderOrder(layers).map((layer) => layer.id).join(',');
};

export const selectWorkspaceRasterProgress = (
  state: KernelState,
  workspaceId: string
): string => {
  const workspace = state.inventory.StudioWorkspace.registry[workspaceId];
  const layerIds = Array.isArray(workspace?.layerIds) ? workspace.layerIds : [];
  const projectId = typeof workspace?.projectId === 'string' ? workspace.projectId.trim() : '';
  let total = 0;
  let loaded = 0;
  for (const layerId of layerIds) {
    const layer = state.inventory.StudioLayer.registry[layerId];
    if (!layer || !projectId || layer.projectId.trim() !== projectId || getLayerWorkspaceId(layer) !== workspaceId) continue;
    if (!layer.visible || layer.type || !layer.imageSrc) continue;
    total += 1;
    if (layer.imageData || layer.loadError) loaded += 1;
  }
  return loaded < total ? `${loaded} / ${total}` : '';
};
